/**
 * WhatsApp no Portal do Condômino (0.1.87) — o morador AUTORIZA receber avisos.
 *
 * Aparece só quando o WhatsApp do sistema está configurado no servidor. O morador
 * confirma o celular, lê o texto e autoriza; desligar é um toque (ou responder SAIR
 * no WhatsApp). O servidor grava quem, quando, qual número e qual texto (LGPD).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";

interface Estado {
  disponivel: boolean;
  ativo: boolean;
  telefone: string;
  telefone_sugerido: string;
  texto: string;
  texto_versao: string;
  aceito_em: string | null;
}

/** 5551996961717 → (51) 99696-1717 */
function formatar(tel: string): string {
  const d = tel.replace(/\D/g, "").replace(/^55/, "");
  return d.length === 11 ? `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}` : tel;
}

export function WhatsappPortal({ condominioId }: { condominioId: number }) {
  const qc = useQueryClient();
  const chave = ["whatsapp", "consentimento", condominioId];
  const { data } = useQuery<Estado>({
    queryKey: chave,
    queryFn: () => api.get<Estado>(`/condominios/${condominioId}/whatsapp/consentimento`),
  });
  const [aberto, setAberto] = useState(false);
  const [telefone, setTelefone] = useState("");
  const [concordo, setConcordo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (data) setTelefone(formatar(data.telefone || data.telefone_sugerido || ""));
  }, [data]);

  const gravar = useMutation({
    mutationFn: (ativo: boolean) =>
      api.put<Estado>(`/condominios/${condominioId}/whatsapp/consentimento`, { ativo, telefone }),
    onSuccess: (novo) => {
      qc.setQueryData(chave, novo);
      setErro(null);
      setAberto(false);
      setConcordo(false);
    },
    onError: (e: unknown) =>
      setErro(e instanceof ApiError
        ? ((e.body as { detail?: string })?.detail ?? e.message)
        : "Não foi possível salvar agora."),
  });

  if (!data || !data.disponivel) return null;

  return (
    <div className="mb-4 rounded-xl bg-emerald-50 border border-emerald-200 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-emerald-900 min-w-0">
          <MessageCircle size={18} className="text-emerald-700 flex-shrink-0" />
          <span className="truncate">
            {data.ativo
              ? <>Avisos no WhatsApp: <b>{formatar(data.telefone)}</b></>
              : "Receba os avisos também pelo WhatsApp"}
          </span>
        </div>
        {data.ativo ? (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={() => setAberto((v) => !v)}
              className="text-xs px-2.5 py-1.5 rounded-md border border-emerald-300 text-emerald-800 bg-white">
              Trocar número
            </button>
            <button onClick={() => gravar.mutate(false)} disabled={gravar.isPending}
              className="text-xs px-2.5 py-1.5 rounded-md border border-emerald-300 text-emerald-800 bg-white disabled:opacity-50">
              Desligar
            </button>
          </div>
        ) : (
          <button onClick={() => setAberto((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-700 text-white flex-shrink-0">
            <MessageCircle size={13} /> Ativar
          </button>
        )}
      </div>

      {aberto && (
        <div className="mt-3 space-y-2">
          <label className="block">
            <span className="block text-xs text-emerald-900 mb-1">Seu celular com DDD</span>
            <input value={telefone} onChange={(e) => setTelefone(e.target.value)}
              inputMode="tel" placeholder="(51) 99999-1234"
              className="w-full sm:w-64 px-2 py-1.5 border border-emerald-300 rounded-md text-sm bg-white" />
          </label>
          <p className="text-xs text-slate-700 bg-white border border-emerald-200 rounded-md p-2">{data.texto}</p>
          <label className="flex items-start gap-2 text-xs text-emerald-900">
            <input type="checkbox" checked={concordo} onChange={(e) => setConcordo(e.target.checked)} className="mt-0.5" />
            Li e autorizo.
          </label>
          <button onClick={() => gravar.mutate(true)} disabled={!concordo || !telefone.trim() || gravar.isPending}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-700 text-white disabled:opacity-40">
            {gravar.isPending && <Loader2 size={13} className="animate-spin" />}
            {data.ativo ? "Salvar novo número" : "Autorizar e ativar"}
          </button>
        </div>
      )}
      {erro && <p className="mt-1.5 text-xs text-rose-600">{erro}</p>}
    </div>
  );
}
